import React from "react";
import { useTranslation } from "react-i18next";
import { useRetrieveCapacity } from "@planzone/view-models";
import { CapacityGranularity, CapacitySort, Filter } from "@planzone/dto";
import { UseCases } from "@planzone/core";

import { WorkspaceContext } from "@scenes/workspaceContext";
import { usePermissions } from "@scenes/safeguard";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { AllocationUnit } from "@common/allocationUnit";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Toolbar } from "./toolbar";
import { Table } from "./table";
import { Props } from "./types";
import styles from "./styles.scss";

const PAGE_SIZE = 20;

function CapacityPage(_: Props) {
  const { t } = useTranslation();
  const [granularity, setGranularity] =
    React.useState<CapacityGranularity>("MONTH");
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [sort, setSort] = React.useState<CapacitySort>("TYPE");
  const [filter, setFilter] = React.useState<Filter>({});
  const { canAdjustCapacity } = usePermissions([UseCases.adjustCapacity]);
  const from = `${year}-01-01`;
  const to = `${year}-12-31`;
  const requestFilter = Object.keys(filter).length > 0 ? filter : undefined;
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    content,
    currentPage,
    canLoadMore,
    retrieveCapacity,
  } = useRetrieveCapacity(
    from,
    to,
    granularity,
    requestFilter,
    sort,
    PAGE_SIZE,
  );

  const retrieveCurrentPage = () =>
    retrieveCapacity({
      from,
      to,
      granularity,
      filter: requestFilter,
      sort,
      page: currentPage,
      size: PAGE_SIZE,
    });

  return (
    <WorkspaceContext type="planzone">
      <div className={styles.capacityPage}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("capacityPage.title")}</h1>
          <div className={styles.profileNavigation}>
            <AssistantShortcut />
            <Notifications />
            <ProfileNavigation />
          </div>
        </div>
        <div className={styles.menu}>
          <Toolbar
            granularity={granularity}
            year={year}
            sort={sort}
            filter={filter}
            setGranularity={setGranularity}
            setYear={setYear}
            setSort={setSort}
            setFilter={setFilter}
          />
          <p className={styles.unit}>
            {t("capacityPage.toolbar.unit")}
            {" : "}
            <AllocationUnit format="text" amount={2} />
          </p>
        </div>
        <div className={styles.content}>
          {isRequestFailure && content.length === 0 ? (
            <div className={styles.error}>
              <p>{t("capacityPage.error")}</p>
              <p className={styles.retry} onClick={retrieveCurrentPage}>
                {t("capacityPage.retry")}
              </p>
            </div>
          ) : isRequestSuccess && content.length === 0 ? (
            <div className={styles.empty}>
              <p>{t("capacityPage.empty")}</p>
            </div>
          ) : (
            <InfiniteScroll
              id="capacity"
              className={styles.tableContainer}
              canFetchOnIdle
            >
              {() => (
                <>
                  <Table
                    from={from}
                    to={to}
                    granularity={granularity}
                    resources={content}
                    isLoadingMore={isRequestPending}
                    canEdit={canAdjustCapacity}
                  />
                  <Flag
                    name="capacityFlag"
                    isDisabled={isRequestPending || !canLoadMore}
                    retrieveItems={retrieveCurrentPage}
                  />
                </>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </WorkspaceContext>
  );
}

export { CapacityPage };
